import React from "react";

/**
 * Smart Health Report — cover page + fully dynamic grouped result pages.
 * Page 1 = 794px sheet (logo header, photo LEFT + QR RIGHT, patient table,
 *   Health Advisory 2-col grid, Vital Indicator Color Guide single row).
 * Pages 2..N = dynamic groups from normalizeSmartReport (`grouped_results`).
 *   Heading = group_name, sub-heading = item sub-group (when present),
 *   cards = parameter_name + value/unit + remarks. 30 cards/page, packed.
 * Last page = doctor consultation.
 * Data comes from normalizeSmartReport (member + grouped_results API).
 * OCR-ready: ocr_reports/ocr_results arrive as extra groups via the normalizer.
 * Branding: XRAi digital only (no MediBuddy). Logo: /campanylogo.jpeg
 * (public folder) with text fallback.
 */

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function XraiLogoImg({ height = 52 }) {
  const [err, setErr] = React.useState(false);
  if (!err) {
    return (
      <img
        src="/campanylogo.jpeg"
        alt="XRAi digital"
        onError={() => setErr(true)}
        style={{ height, maxWidth: "100%", objectFit: "contain" }}
      />
    );
  }
  return (
    <span style={{ fontWeight: 800, fontSize: 26, color: "#0d1b2a" }}>
      XRA<span style={{ color: "#1565C0" }}>i</span> digital
    </span>
  );
}

function XraiLogoSmall({ height = 35 }) {
  const [err, setErr] = React.useState(false);
  if (!err) {
    return (
      <img
        src="/campanylogo.jpeg"
        alt="XRAi digital"
        onError={() => setErr(true)}
        style={{ maxWidth: 190, height, marginBottom: 4, objectFit: "contain" }}
      />
    );
  }
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: "bold", color: "white", letterSpacing: 0.3 }}>XRAi digital</div>
      <div style={{ fontSize: 7.5, textTransform: "uppercase", color: "#7a9bb5", letterSpacing: 1, marginTop: 2 }}>
        Smart Health Report
      </div>
    </div>
  );
}

const ADVISORY = [
  { icon: "🥗", bg: "#eef3fb", en: "Eat a Balanced Diet", desc: "Eat a balanced diet rich in fruits and vegetables.", hi: "संतुलित आहार लें, फल और सब्जियाँ शामिल करें।" },
  { icon: "🏃", bg: "#e6f9f2", en: "Exercise Regularly", desc: "At least 30 minutes of physical activity daily.", hi: "रोज़ कम से कम 30 मिनट व्यायाम करें।" },
  { icon: "💧", bg: "#fff8ec", en: "Stay Hydrated", desc: "Drink plenty of water throughout the day.", hi: "दिन भर पर्याप्त पानी पिएं।" },
  { icon: "😴", bg: "#e6f9f2", en: "Get Adequate Sleep", desc: "Ensure 7–8 hours of sleep every night.", hi: "हर रात 7–8 घंटे की नींद लें।" },
  { icon: "🚭", bg: "#fff0f0", en: "Healthy Lifestyle Habits", desc: "Avoid tobacco, alcohol, and all harmful substances for a healthier life.", hi: "तंबाकू, शराब और हानिकारक पदार्थों से दूर रहें।" },
  { icon: "🧼", bg: "#f0f7ff", en: "Maintain Personal Hygiene", desc: "Maintain good personal hygiene, including regular handwashing.", hi: "अच्छी व्यक्तिगत स्वच्छता बनाए रखें, जैसे नियमित रूप से हाथ धोना।" },
];

function AdvisoryCell({ item, right, bottom }) {
  return (
    <div
      style={{
        padding: "7px 12px",
        borderRight: right ? "1px solid #dde3ea" : undefined,
        borderBottom: bottom ? "1px solid #dde3ea" : undefined,
        display: "flex",
        gap: 8,
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: item.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 13,
        }}
      >
        {item.icon}
      </div>
      <div>
        <div style={{ fontSize: 10.5, fontWeight: "bold" }}>{item.en}</div>
        <div style={{ fontSize: 9 }}>{item.desc}</div>
        <div style={{ fontSize: 9, fontStyle: "italic" }}>{item.hi}</div>
      </div>
    </div>
  );
}

const C_GREEN = "#00C47A";
const C_GREEN_TXT = "#0B8F6A";
const C_ORANGE = "#FB8C00";
const C_RED = "#E53935";
const C_CRITICAL = "#B71C1C";

/** Shared footer: company details + page number, pinned to page bottom. */
function ReportFooter({ label, pageNo, totalPages, style }) {
  return (
    <div className="footer" style={style}>
      <div className="footer-addr">C-406, 4th Floor, Nirvana Courtyard, Sector-50, Gurgaon, Haryana, India</div>
      <div className="footer-row">
        <span>www.xraidigital.com | info@xraidigital.com | Tel: 0124-4354012</span>
        <span>{label} | Page {pageNo} / {totalPages}</span>
      </div>
    </div>
  );
}

function PrescriptionText({ text }) {
  const lines = String(text ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "");
  if (lines.length === 0) return "N/A";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {lines.map((line, i) => {
        const numbered = line.match(/^(\d+)[.)]\s*(.*)$/);
        if (numbered) {
          return (
            <div key={i} style={{ display: "flex", gap: 6 }}>
              <span style={{ flexShrink: 0, minWidth: 14 }}>{numbered[1]}.</span>
              <span style={{ flex: 1 }}>{numbered[2]}</span>
            </div>
          );
        }
        return <div key={i}>{line}</div>;
      })}
    </div>
  );
}

const css2 = `
* { box-sizing: border-box; margin: 0; padding: 0; }
.rc { width: 794px; background: #fff; font-family: Arial, sans-serif; color: #3D5166; padding: 10px; margin: 0 auto; }
.inner { border: 1px solid #DDE3EA; overflow: hidden; display: flex; flex-direction: column; min-height: 1090px; }
.rh { background: #0d1b2a; color: white; padding: 11px 18px; display: flex; justify-content: space-between; align-items: center; }
.brand-date { font-size: 17.5px; margin-top: 4px; color: white; }
.report-title { font-size: 22px; letter-spacing: 2.5px; text-transform: uppercase; font-weight: bold; color: white; }
.sh { background: #F5F7FA; border-left: 5px solid #00C47A; padding: 6px 14px; margin: 5px 0; font-size: 9.5px; font-weight: bold; color: #0b8f6a; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid #DDE3EA; }
.cons-grid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #DDE3EA; }
.cons-cell { padding: 10px 14px; border-right: 1px solid #DDE3EA; }
.cons-cell:last-child { border-right: none; }
.cons-full { padding: 10px 14px; border-bottom: 1px solid #DDE3EA; }
.cons-full:last-child { border-bottom: none; }
.cons-lbl { font-size: 8.5px; color: #8898AA; display: block; margin-bottom: 3px; line-height: 1.3; }
.cons-val { font-size: 12px; font-weight: bold; color: #1a1a1a; line-height: 1.4; white-space: pre-line; }
.rx-table { width: 100%; border-collapse: collapse; font-size: 10px; }
.rx-table th { background: #F5F7FA; color: #3D5166; font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.4px; padding: 7px 10px; text-align: left; border-bottom: 1px solid #DDE3EA; }
.rx-table td { padding: 8px 10px; border-bottom: 1px solid #f2f2f2; color: #1a1a1a; vertical-align: top; }
.rx-table tr:last-child td { border-bottom: none; }
.footer { background: #0d1b2a; color: #7a9bb5; font-size: 7.5px; padding: 5px 16px; display: flex; flex-direction: column; gap: 2px; margin-top: auto; }
.footer-addr { text-align: center; width: 100%; }
.footer-row { display: flex; justify-content: space-between; align-items: center; width: 100%; gap: 8px; }
.add-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 10px 14px; }
.add-card { border: 1px solid #DDE3EA; border-radius: 6px; padding: 8px 10px; background: #fff; min-width: 0; }
.add-name { font-size: 10.5px; font-weight: bold; color: #3D5166; line-height: 1.3; word-break: break-word; white-space: normal; }
.add-val { font-size: 16px; font-weight: 800; color: #1a1a1a; margin-top: 2px; line-height: 1.2; word-break: break-word; white-space: normal; }
.add-unit { font-size: 9px; color: #5a6a7a; font-weight: normal; }
.add-meta { font-size: 8.5px; color: #8898AA; margin-top: 3px; line-height: 1.4; word-break: break-word; white-space: normal; }
.add-sub { grid-column: 1 / -1; font-size: 10px; font-weight: bold; color: #0b8f6a; background: #E6F9F2; border-radius: 4px; padding: 5px 10px; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.4px; }
.add-empty { grid-column: 1 / -1; text-align: center; color: #6b7280; font-size: 11px; padding: 18px 10px; }
`;

/** Max result cards per dynamic page (same density as old additional pages). */
const GROUP_PAGE_SIZE = 30;

/**
 * Pack groups into pages — fixed report sequence, one section per page,
 * EXCEPT Audio + Opto + Dental (shared page) and PFT + ECG + Pregnancy
 * (shared page) via the normalizer's `page` slot. A section larger than GROUP_PAGE_SIZE
 * continues on following pages with the header repeated.
 * Returns [{ blocks: [{ group_name, items[] }] }].
 */
function packGroupPages(groups) {
  const pages = [];
  let cur = null; // { slot, blocks, count }
  const flush = () => {
    if (cur && cur.blocks.length > 0) pages.push({ blocks: cur.blocks });
    cur = null;
  };
  groups.forEach((g) => {
    const items = g.items || [];
    if (items.length === 0) return;
    const slot = g.page ?? 99;
    for (let i = 0; i < items.length; i += GROUP_PAGE_SIZE) {
      const chunk = items.slice(i, i + GROUP_PAGE_SIZE);
      if (!cur || cur.slot !== slot || cur.count + chunk.length > GROUP_PAGE_SIZE) {
        flush();
        cur = { slot, blocks: [], count: 0 };
      }
      const last = cur.blocks[cur.blocks.length - 1];
      if (last && last.group_name === g.group_name) last.items.push(...chunk);
      else cur.blocks.push({ group_name: g.group_name, items: [...chunk] });
      cur.count += chunk.length;
    }
  });
  flush();
  return pages;
}

/** Cluster block items by sub-group (when present) for sub-headings. */
function clusterBySubGroup(items) {
  const order = [];
  const map = new Map();
  items.forEach((it) => {
    const key = it && it.sub_group != null && String(it.sub_group).trim() !== ""
      ? String(it.sub_group).trim()
      : "__none__";
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key).push(it);
  });
  return order.map((k) => ({ subGroup: k === "__none__" ? null : k, items: map.get(k) }));
}

const SmartHealthReport = React.forwardRef(function SmartHealthReport({ report, qrDataUrl }, ref) {
  if (!report) return null;
  const { member, screeningDate, consultation } = report;
  // Groups from normalizer; legacy fallback converts old additionalResults.
  let groups = Array.isArray(report.groups) ? report.groups : [];
  if (groups.length === 0 && Array.isArray(report.additionalResults) && report.additionalResults.length > 0) {
    groups = [{ group_id: null, group_name: "Additional Test Results", items: report.additionalResults }];
  }
  const groupPages = packGroupPages(groups);
  // Always render at least one grouped page (empty state) so numbering stays sane.
  const renderGroupPages = groupPages.length > 0 ? groupPages : [{ blocks: [] }];
  const totalPages = 1 + renderGroupPages.length + 1;
  const photo = member.photo || null;
  const cons = consultation || null;
  const hasConsult = !!cons && !!(
    cons.chiefComplaints || cons.durationOfSymptoms || cons.medicalHistory || cons.clinicalFindings ||
    cons.diagnosis || cons.treatmentPlan || (cons.prescription && cons.prescription.length)
  );

  const disp = (v) => {
    const s = String(v ?? "").trim();
    return s === "" ? "N/A" : s;
  };

  const rows1 = [
    ["Unique Patient ID / अद्वितीय आईडी", disp(member.member_id)],
    ["Name / नाम", disp(member.name)],
    ["Age / आयु", member.age != null && String(member.age).trim() !== "" ? `${member.age} year/वर्ष` : "N/A"],
    ["Gender / लिंग", disp(member.gender)],
    ["Phone / फ़ोन", disp(member.phone)],
    ["Account Number / खाता संख्या", disp(member.loan_number)],
    ["Screening Date / जांच की तिथि", disp(screeningDate)],
  ];

  const todayIN = new Date().toLocaleDateString("en-IN");

  const renderHeader = (
    <div>
      <XraiLogoSmall height={35} />
      <div className="brand-date">Date: {screeningDate && screeningDate !== "N/A" ? screeningDate : todayIN}</div>
    </div>
  );
  const renderMemberRight = (
    <div style={{ textAlign: "right" }}>
      <div style={{ fontSize: 12, fontWeight: "bold", color: "white", marginTop: 1 }}>{disp(member.name)}</div>
      <div style={{ fontSize: 9.5, color: "white", marginTop: 1 }}>Member ID: {disp(member.member_id)}</div>
      {member.loan_number ? <div style={{ fontSize: 9.5, color: "white" }}>Account no: {member.loan_number}</div> : null}
    </div>
  );

  return (
    <div ref={ref} className="smart-health-report">
      <style>{css2}</style>

      {/* ==================== PAGE 1 (cover, unchanged) ==================== */}
      {/* NOTE: keep this page within one A4 height (~1123px @96dpi) — overflow
          spills a blank page into the html2pdf output after page 1. */}
      <div
        style={{
          width: 794,
          padding: 24,
          fontFamily: "Arial, sans-serif",
          boxSizing: "border-box",
          position: "relative",
          border: "0px solid #000",
          margin: "auto",
          background: "#fff",
          pageBreakAfter: "always",
          display: "flex",
          flexDirection: "column",
          minHeight: 1090,
        }}
      >
        {/* Content wrapper (keeps table layout intact inside flex column) */}
        <div>
        {/* Logo + Photo + QR in one line (photo centered) */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <div style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "10px 16px", textAlign: "center", background: "#f9fafc", borderRadius: 8 }}>
              <XraiLogoImg height={48} />
              <div style={{ marginTop: 6, fontSize: 14, fontWeight: 800, color: "#0d1b2a", whiteSpace: "nowrap" }}>Comprehensive Health Report</div>
            </div>
          </div>
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <div style={{ width: 150, height: 150, borderRadius: "50%", overflow: "hidden", border: "4px solid #4285F4", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {photo ? (
                /* eslint-disable-next-line jsx-a11y/img-redundant-alt */
                <img src={photo} alt="Profile Photo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: 44, fontWeight: 800, color: "#0d1b2a" }}>{initialsOf(member.name)}</span>
              )}
            </div>
          </div>
          <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <div style={{ textAlign: "center" }}>
              <p style={{ margin: "0 0 6px 0", fontWeight: "bold", fontSize: 13 }}>Scan for Details</p>
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR Code" style={{ width: 140, height: 140, border: "1px solid #000" }} />
              ) : (
                <div style={{ width: 140, height: 140, border: "1px solid #000", display: "flex", alignItems: "center", justifyContent: "center", color: "#8898AA" }}>
                  QR
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Patient Details Table */}
        <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
          <tbody>
            {rows1.map(([label, value], i) => (
              <tr key={i}>
                <td style={{ padding: 7, border: "1px solid #000", backgroundColor: "#f0f0f0", fontWeight: "bold", width: "40%" }}>{label}</td>
                <td style={{ padding: 7, border: "1px solid #000" }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Health Instructions Section */}
        <div style={{ marginTop: 10, fontFamily: "Arial, sans-serif" }}>
          <div style={{ background: "linear-gradient(90deg,#0d1b2a 0%,#1565C0 100%)", padding: "7px 16px", borderRadius: "4px 4px 0 0", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: "bold", color: "#fff", letterSpacing: 0.4 }}>Health Advisory</span>
            <span style={{ fontSize: 13, color: "#fff" }}>/ स्वास्थ्य निर्देश</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", border: "1px solid #dde3ea", borderTop: "none", borderRadius: "0 0 4px 4px", overflow: "hidden" }}>
            {ADVISORY.map((item, i) => (
              <AdvisoryCell key={item.en} item={item} right={i % 2 === 0} bottom={i < 4} />
            ))}
          </div>
        </div>

        {/* Color Meaning Legend - Compact Single Row */}
        <div style={{ marginTop: 8, fontFamily: "Arial, sans-serif" }}>
          <div style={{ background: "linear-gradient(90deg,#0d1b2a 0%,#1565C0 100%)", padding: "6px 16px", borderRadius: "4px 4px 0 0", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, fontWeight: "bold", color: "#fff", letterSpacing: 0.4 }}>Vital Indicator Color Guide</span>
            <span style={{ fontSize: 11, color: "#fff" }}>/ रंग संकेतक मार्गदर्शिका</span>
          </div>
          <div style={{ border: "1px solid #dde3ea", borderTop: "none", borderRadius: "0 0 4px 4px", padding: "8px 16px", background: "#f9fafc", display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, flex: 1 }}>
              <div style={{ width: 13, height: 13, borderRadius: "50%", background: C_GREEN }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: "bold", color: C_GREEN_TXT }}>Normal / सामान्य</div>
                <div style={{ fontSize: 8.5, color: "#555" }}>Within healthy range</div>
              </div>
            </div>
            <div style={{ width: 1, height: 32, background: "#dde3ea" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 7, flex: 1 }}>
              <div style={{ width: 13, height: 13, borderRadius: "50%", background: C_ORANGE }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: "bold", color: "#E65100" }}>Warning / चेतावनी</div>
                <div style={{ fontSize: 8.5, color: "#555" }}>Slightly outside range</div>
              </div>
            </div>
            <div style={{ width: 1, height: 32, background: "#dde3ea" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 7, flex: 1 }}>
              <div style={{ width: 13, height: 13, borderRadius: "50%", background: C_RED }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: "bold", color: C_CRITICAL }}>Out of Range / सीमा से बाहर</div>
                <div style={{ fontSize: 8.5, color: "#555" }}>Abnormal condition</div>
              </div>
            </div>
            <div style={{ width: 1, height: 32, background: "#dde3ea" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 7, flex: 1 }}>
              <div style={{ background: "#FFEBEE", padding: "2px 5px", borderRadius: 3, fontWeight: "bold", fontSize: 10, color: C_CRITICAL }}>⚠️</div>
              <div>
                <div style={{ fontSize: 10, fontWeight: "bold", color: C_CRITICAL }}>Critical / गंभीर</div>
                <div style={{ fontSize: 8.5, color: "#555" }}>Immediate medical attention needed</div>
              </div>
            </div>
          </div>
        </div>

        </div>
        {/* Page 1 footer — pinned to page bottom */}
        <ReportFooter
          label={`Member ID: ${disp(member.member_id)} | Demography`}
          pageNo={1}
          totalPages={totalPages}
          style={{ borderRadius: 4 }}
        />
      </div>

      {/* ==================== PAGES 2..N — GROUPED TEST RESULTS (dynamic) ==================== */}
      {renderGroupPages.map((page, pageIdx) => {
        const pageNo = 2 + pageIdx;
        return (
          <div className="rc" key={`group-page-${pageNo}`} style={pageIdx > 0 ? { pageBreakBefore: "always" } : undefined}>
            <div className="inner">
              <div className="rh">
                {renderHeader}
                <div style={{ textAlign: "center" }}>
                  <div className="report-title">TEST RESULTS</div>
                </div>
                {renderMemberRight}
              </div>

              {page.blocks.length === 0 ? (
                <>
                  <div className="sh">TEST RESULTS - परीक्षण परिणाम</div>
                  <div className="add-grid">
                    <div className="add-empty">No test results available.</div>
                  </div>
                </>
              ) : (
                page.blocks.map((block, bi) => {
                  const clusters = clusterBySubGroup(block.items);
                  const showSubs = clusters.length > 1 || (clusters.length === 1 && clusters[0].subGroup);
                  return (
                    <React.Fragment key={`${block.group_name}-${bi}`}>
                      <div className="sh">{disp(block.group_name)}</div>
                      <div className="add-grid">
                        {clusters.map((cl, ci) => (
                          <React.Fragment key={`${block.group_name}-sub-${ci}`}>
                            {showSubs && cl.subGroup ? (
                              <div className="add-sub">{disp(cl.subGroup)}</div>
                            ) : null}
                            {cl.items.map((r, i) => {
                              const val = r.value == null || String(r.value).trim() === "" ? "N/A" : String(r.value).trim();
                              const unit = r.unit != null && String(r.unit).trim() !== "" ? String(r.unit).trim() : "";
                              const remarks = r.remarks != null && String(r.remarks).trim() !== "" ? String(r.remarks).trim() : "";
                              return (
                                <div className="add-card" key={`${r.parameter ?? r.parameter_code ?? r.parameter_name}-${i}`}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                    <span className="add-name" style={{ flex: 1, minWidth: 0 }}>{disp(r.parameter_name)}</span>
                                    <span className="add-val" style={{ flexShrink: 0 }}>{val}{unit ? <span className="add-unit"> {unit}</span> : null}</span>
                                  </div>
                                  {remarks ? <div className="add-meta">{remarks}</div> : null}
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                    </React.Fragment>
                  );
                })
              )}

              <ReportFooter
                label={`Member ID: ${disp(member.member_id)} | Test Results`}
                pageNo={pageNo}
                totalPages={totalPages}
              />
            </div>
          </div>
        );
      })}

      {/* ==================== LAST PAGE — DOCTOR CONSULTATION ==================== */}
      <div className="rc" style={{ pageBreakBefore: "always" }}>
        <div className="inner">
          {/* HEADER (same as other pages) */}
          <div className="rh">
            {renderHeader}
            <div style={{ textAlign: "center" }}>
              <div className="report-title">DOCTOR CONSULTATION</div>
            </div>
            {renderMemberRight}
          </div>

          {/* COMPLAINTS / HISTORY */}
          <div className="sh">CONSULTATION SUMMARY - परामर्श सारांश</div>
          {!hasConsult ? (
            <div className="cons-full">
              <span className="cons-lbl">Doctor consultation</span>
              <div className="cons-val" style={{ fontWeight: 500, color: "#6b7280" }}>
                No doctor consultation recorded for this visit.
              </div>
            </div>
          ) : (
            <>
              <div className="cons-grid">
                <div className="cons-cell">
                  <span className="cons-lbl">Chief Complaints / मुख्य शिकायतें</span>
                  <div className="cons-val">{disp(cons.chiefComplaints)}</div>
                </div>
                <div className="cons-cell">
                  <span className="cons-lbl">Duration of Symptoms / लक्षणों की अवधि</span>
                  <div className="cons-val">{disp(cons.durationOfSymptoms)}</div>
                </div>
              </div>
              <div className="cons-grid">
                <div className="cons-cell">
                  <span className="cons-lbl">Medical History / चिकित्सा इतिहास</span>
                  <div className="cons-val" style={{ fontWeight: 500 }}>{disp(cons.medicalHistory)}</div>
                </div>
                <div className="cons-cell">
                  <span className="cons-lbl">Clinical Findings / नैदानिक निष्कर्ष</span>
                  <div className="cons-val" style={{ fontWeight: 500 }}>{disp(cons.clinicalFindings)}</div>
                </div>
              </div>
              <div className="cons-full">
                <span className="cons-lbl">Diagnosis / निदान</span>
                <div className="cons-val">{disp(cons.diagnosis)}</div>
              </div>
              <div className="cons-full" style={{ borderBottom: "1px solid #DDE3EA" }}>
                <span className="cons-lbl">Treatment Plan / उपचार योजना</span>
                <div className="cons-val" style={{ fontWeight: 500 }}>{disp(cons.treatmentPlan)}</div>
              </div>
            </>
          )}

          {/* PRESCRIPTION */}
          <div className="sh">PRESCRIPTION - नुस्खा</div>
          {!hasConsult || !cons.prescription || cons.prescription.length === 0 ? (
            <div className="cons-full">
              <div className="cons-val" style={{ fontWeight: 500, color: "#6b7280" }}>
                {hasConsult ? "No medicines prescribed." : "N/A"}
              </div>
            </div>
          ) : (
            <div style={{ padding: "4px 14px 10px" }}>
              <table className="rx-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Medicine / दवा</th>
                    <th>Dosage / Frequency</th>
                    <th>Instructions / निर्देश</th>
                  </tr>
                </thead>
                <tbody>
                  {cons.prescription.map((rx, i) => {
                    const doseFreq = [rx.dosage, rx.frequency, rx.duration].filter(Boolean).join(" · ") || null;
                    // Item may carry only `instructions` (e.g. {instructions:"try for 1 week"})
                    // → show it full-width instead of blank N/A cells.
                    if (!rx.medicine && !doseFreq) {
                      return (
                        <tr key={i}>
                          <td style={{ color: "#6b7280" }}>{i + 1}</td>
                          <td colSpan={3} style={{ fontWeight: 500 }}><PrescriptionText text={rx.instructions} /></td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={i}>
                        <td style={{ color: "#6b7280" }}>{i + 1}</td>
                        <td style={{ fontWeight: "bold" }}>{rx.medicine || "—"}</td>
                        <td>{doseFreq || "—"}</td>
                        <td><PrescriptionText text={rx.instructions} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <ReportFooter
            label={`Member ID: ${disp(member.member_id)} | Doctor Consultation`}
            pageNo={totalPages}
            totalPages={totalPages}
          />
        </div>
      </div>
    </div>
  );
});

export default SmartHealthReport;
